import React from 'react';
import { Bid } from '../../types/bid.types';
import { formatDistanceToNow } from 'date-fns';

interface BidHistoryListProps {
  bids: Bid[];
  currentUserId?: string;
  showFullNames?: boolean; // Admin view
}

/**
 * Group bids by bidder and show count
 */
interface BidderSummary {
  bidderId: string;
  bidderName: string;
  bidderNumber?: string; // PUBLIC identity — shown as "Bidder #3607"
  bidCount: number;
  highBid: number;
  lastBidTime: Date;
  isWinning: boolean;
  isCurrentUser: boolean;
}

const groupBidsByBidder = (bids: Bid[], currentUserId?: string): BidderSummary[] => {
  const bidderMap = new Map<string, BidderSummary>();
  
  bids.forEach(bid => {
    const existing = bidderMap.get(bid.bidderId);
    
    if (existing) {
      existing.bidCount++;
      if (bid.amount > existing.highBid) {
        existing.highBid = bid.amount;
      }
      if (bid.placedAt > existing.lastBidTime) {
        existing.lastBidTime = bid.placedAt;
      }
      if (bid.isWinning) {
        existing.isWinning = true;
      }
    } else {
      bidderMap.set(bid.bidderId, {
        bidderId: bid.bidderId,
        bidderName: bid.bidderName,
        bidderNumber: bid.bidderNumber,
        bidCount: 1,
        highBid: bid.amount,
        lastBidTime: bid.placedAt,
        isWinning: bid.isWinning,
        isCurrentUser: currentUserId === bid.bidderId,
      });
    }
  });
  
  // Convert to array and sort by high bid (descending)
  return Array.from(bidderMap.values()).sort((a, b) => b.highBid - a.highBid);
};

export const BidHistoryList: React.FC<BidHistoryListProps> = ({
  bids,
  currentUserId,
  showFullNames = false,
}) => {
  if (bids.length === 0) {
    return (
      <div className="text-center py-4 text-gray-600">
        No bids yet
      </div>
    );
  }

  const bidderSummaries = groupBidsByBidder(bids, currentUserId);

  return (
    <div className="space-y-2">
      {/* Header */}
      <div className="grid grid-cols-4 gap-2 px-3 py-2 bg-gray-100 rounded-lg text-sm font-semibold text-gray-700">
        <div>Bidder</div>
        <div className="text-center">Bids</div>
        <div className="text-right">High Bid</div>
        <div className="text-right">Last Bid</div>
      </div>

      {/* Bidder Rows */}
      <div className="space-y-1 max-h-96 overflow-y-auto">
        {bidderSummaries.map((summary) => (
          <div
            key={summary.bidderId}
            className={`grid grid-cols-4 gap-2 p-3 rounded-lg border ${
              summary.isWinning
                ? 'bg-green-50 border-green-200'
                : summary.isCurrentUser
                ? 'bg-blue-50 border-blue-200'
                : 'bg-gray-50 border-gray-200'
            }`}
          >
            <div>
              <p className="font-semibold text-sm">
                {summary.isCurrentUser
                  ? 'You'
                  : showFullNames
                    ? summary.bidderName
                    : `Bidder #${summary.bidderNumber ?? '—'}`
                }
              </p>
              {summary.isWinning && (
                <span className="text-xs bg-green-500 text-white px-2 py-0.5 rounded mt-1 inline-block">
                  WINNING
                </span>
              )}
            </div>
            
            <div className="text-center">
              <p className="font-semibold">{summary.bidCount}</p>
            </div>
            
            <div className="text-right">
              <p className="font-bold text-primary-600">
                ${(summary.highBid / 100).toFixed(2)}
              </p>
            </div>
            
            <div className="text-right">
              <p className="text-xs text-gray-600">
                {formatDistanceToNow(summary.lastBidTime, { addSuffix: true })}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Tie Bid Notice */}
      <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-xs text-blue-800">
          ℹ️ In the case of a tie bid, precedence is given to the earliest bid
        </p>
      </div>
    </div>
  );
};